// src/utils/jsonSync.js
//
// Módulo aislado para la tecla "Botón Inteligente de Sincronización de JSON
// en Login" (ver Analisis_Viabilidad_Sync_JSON_Login.docx). No importa nada
// de App.js ni modifica su comportamiento: abre el mismo IndexedDB
// (POSDatabase / store "data") de forma independiente, usando exactamente
// el mismo formato de clave `${property}/${fileName}` que ya usa
// fetchData() en App.js, para que lo que este módulo guarde sea consumido
// de inmediato por el resto del sistema sin tocarlo.
//
// Alcance (decidido explícitamente, ver requerimiento): SOLO
//   - employees.json
//   - employees_sec.json
//   - promos.json
//   - products_*.json
// Nada de config.json, revenues.json, devices.json, payments.json, ni datos
// de cheques/pedidos/cortes de caja (esos ni siquiera son JSON).
//
// Backend reutilizado (ya existía, sin usar por nadie): sync_manifest.php
// (metadata con sha256 por archivo) y sync_download.php (descarga puntual
// de un archivo por su path). No se agregó autenticación nueva: se mantiene
// el mismo mecanismo (público) que ya usa el resto del sistema para JSON,
// por decisión explícita del cliente.

import { IP } from "../configuracion.js";

const DB_NAME = "POSDatabase";
const STORE_NAME = "data";
const META_PREFIX = "__syncmeta__";

// Nombres exactos en alcance + patrón de productos.
const SCOPE_EXACT = ["employees.json", "employees_sec.json", "promos.json"];
const SCOPE_PRODUCTS_RE = /^products_.*\.json$/i;

function isInScope(fileName) {
  return SCOPE_EXACT.includes(fileName) || SCOPE_PRODUCTS_RE.test(fileName);
}

function isProductFile(fileName) {
  return SCOPE_PRODUCTS_RE.test(fileName);
}

// ── IndexedDB: misma base/store/forma de clave que App.js (openDB/getFromIndexedDB/saveToIndexedDB) ──
// Se reimplementa aquí, en vez de importarlo de App.js, para mantener este
// módulo completamente aislado (requerimiento: "desarrollo completamente
// encapsulado para no afectar otros módulos").
function openSyncDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
  });
}

async function getKey(key) {
  try {
    const db = await openSyncDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result?.data ?? null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error(`[jsonSync] Error leyendo IndexedDB (${key}):`, error);
    return null;
  }
}

async function setKey(key, data) {
  const db = await openSyncDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put({ key, data });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ── Etiquetas amigables de progreso, según pide el requerimiento ───────────
function progressLabel(fileName) {
  if (fileName === "employees.json") return "Actualizando empleados...";
  if (fileName === "employees_sec.json") return "Actualizando seguridad de empleados...";
  if (fileName === "promos.json") return "Actualizando promociones...";
  if (isProductFile(fileName)) return "Descargando productos...";
  return `Sincronizando ${fileName}...`;
}

/**
 * Compara silenciosamente el JSON local (IndexedDB) contra la nube.
 * Devuelve la lista de archivos distintos (o nuevos) dentro del alcance.
 * No descarga ni modifica nada — solo lee metadata (rápido, liviano).
 *
 * @param {string} property
 * @returns {Promise<Array<{path:string, fileName:string, sha256:string, size:number}>>}
 */
export async function compararConNube(property) {
  if (!property) return [];

  const url = `${IP}/tavpos/backend/sync_manifest.php?property=${encodeURIComponent(
    property
  )}&scope=json_basic`;

  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} al consultar sync_manifest.php`);
  }

  const manifest = await res.json();
  if (!manifest || manifest.ok !== true || !Array.isArray(manifest.files)) {
    throw new Error("Manifiesto de sincronización inválido");
  }

  const diffs = [];
  for (const entry of manifest.files) {
    const fileName = String(entry.path).split("/").pop();
    if (!isInScope(fileName)) continue; // por si el backend algún día cambia, no confiar solo en él

    const metaKey = `${META_PREFIX}/${property}/${fileName}`;
    const meta = await getKey(metaKey);

    if (!meta || meta.sha256 !== entry.sha256) {
      diffs.push({
        path: entry.path,
        fileName,
        sha256: entry.sha256,
        size: entry.size,
      });
    }
  }
  return diffs;
}

/**
 * Descarga y aplica solo los archivos indicados (los que ya se detectaron
 * como distintos). Valida integridad (sha256) antes de reemplazar cualquier
 * dato local. Para employees.json/employees_sec.json/promos.json actualiza
 * directamente la entrada en IndexedDB que ya usa fetchData() en App.js, de
 * modo que el POS la tome de inmediato sin necesidad de borrar datos de
 * navegación. Para products_*.json no se toca IndexedDB (fetchData ya
 * intenta red primero para productos); solo se valida integridad.
 *
 * @param {string} property
 * @param {Array<{path:string, fileName:string, sha256:string}>} diffFiles
 * @param {(msg:string) => void} [onProgress]
 */
export async function sincronizarDiferencias(property, diffFiles, onProgress) {
  if (!property || !Array.isArray(diffFiles) || diffFiles.length === 0) return;

  for (const file of diffFiles) {
    onProgress?.(progressLabel(file.fileName));

    let text;
    try {
      const res = await fetch(
        `${IP}/tavpos/backend/sync_download.php?file=${encodeURIComponent(file.path)}`,
        { method: "GET" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      text = await res.text();
    } catch (err) {
      const wrapped = new Error(`No se pudo descargar el archivo (${err.message})`);
      wrapped.file = file.fileName;
      throw wrapped;
    }

    // Validar integridad con SHA-256 nativo del navegador antes de aceptar el archivo.
    try {
      const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
      const computedHex = Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      if (computedHex !== file.sha256) {
        const err = new Error("El archivo descargado no coincide con el checksum esperado");
        err.file = file.fileName;
        throw err;
      }
    } catch (err) {
      if (err.file) throw err;
      const wrapped = new Error(`Error validando integridad (${err.message})`);
      wrapped.file = file.fileName;
      throw wrapped;
    }

    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      const err = new Error("El archivo descargado no es JSON válido");
      err.file = file.fileName;
      throw err;
    }

    try {
      if (!isProductFile(file.fileName)) {
        // Mismo formato/clave que fetchData() en App.js: dato crudo bajo `${property}/${fileName}`.
        await setKey(`${property}/${file.fileName}`, parsed);
      }
      // Metadata de sincronización (clave propia, no colisiona con la caché real).
      await setKey(`${META_PREFIX}/${property}/${file.fileName}`, {
        sha256: file.sha256,
        size: file.size,
        syncedAt: Date.now(),
      });
    } catch (err) {
      const wrapped = new Error(`No se pudo actualizar la copia local (${err.message})`);
      wrapped.file = file.fileName;
      throw wrapped;
    }
  }

  onProgress?.("Finalizando...");
}
